import { getReserveWithGoogleToken } from "./getReserveWithGoogleToken";
import {
  IClientState,
  IGetRwgConfigResponseV1,
  IGetRwgConfigResponseV2,
  IGetRwgConfigErrorResponse,
  IRouteParams,
  RouteType,
  ITokenRouteParams,
  IAbandonedSessionRouteParams,
  IYelpRouteParams,
} from "../types/App";
import * as Sentry from "@sentry/react";

/**
 * Parses the current URL pathname to determine route type and parameters
 */
export const parseRoute = (): IRouteParams => {
  const pathname = window.location.pathname;
  const splitRoute = pathname.split("/");

  // Yelp route: /yelp/${yelpListingId}
  if (splitRoute.length >= 3 && splitRoute[splitRoute.length - 2] === "yelp") {
    const yelpListingId = splitRoute[splitRoute.length - 1].split("?")[0];
    const yelpToken =
      new URLSearchParams(window.location.search).get("yelp_token") ||
      undefined;

    return {
      type: RouteType.Yelp,
      yelpListingId,
      yelpToken,
    } as IYelpRouteParams;
  }

  // Check for recover-session route pattern: /recover-session/${abandoned_session_id}
  if (
    splitRoute.length >= 3 &&
    splitRoute[splitRoute.length - 2] === "recover-session"
  ) {
    const abandonedSessionId = splitRoute[splitRoute.length - 1].split("?")[0];

    return {
      abandonedSessionId,
      type: RouteType.AbandonedSession,
    } as IAbandonedSessionRouteParams;
  }

  // Normal route - extract token from the last path segment
  const token = splitRoute[splitRoute.length - 1].split("?")[0];
  const isGbpId = token.startsWith("gbp_");

  return {
    isGbpId,
    token,
    type: RouteType.Token,
  } as ITokenRouteParams;
};

/**
 * Fetches rwgConfig data with token or gbpId
 */
const fetchTokenRwgConfig = async (
  params: ITokenRouteParams
): Promise<IClientState> => {
  const { isGbpId, token } = params;

  if (isGbpId) {
    return {
      data: undefined,
      requestSuccess: false,
    };
  }

  const rwgToken = getReserveWithGoogleToken();

  if (!rwgToken) {
    return {
      data: undefined,
      requestSuccess: false,
    };
  }

  const res = await fetch(process.env.REACT_APP_SCHEDULER_API || "", {
    body: JSON.stringify({
      query: `
        query rwgConfig($rwgToken: String! $token: String) {
          rwgConfig(rwgToken: $rwgToken, token: $token) {
            ... on ErrorResponse 
            {
              message
            }
            ... on RwgConfig {
              addressLine
              clientKey
              flowKey
              mediaUrl
              name
              schedulerSrc
            }
          }
        }
      `,
      variables: {
        rwgToken,
        token: isGbpId ? undefined : token,
      },
    }),
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "Content-Type": "application/json",
    },
    method: "POST",
  });

  const resJson = await res.json();

  if (!res.ok) {
    const body = JSON.stringify(resJson);
    Sentry.setExtra("body", { ...resJson.errors });
    Sentry.captureException(
      new Error(`rwgConfig failed with ${res.status}: ${body}`)
    );

    return {
      data: undefined,
      requestSuccess: false,
    };
  }

  if (resJson.errors) {
    const body = JSON.stringify(resJson);
    Sentry.setExtra("body", { ...resJson.errors });
    Sentry.captureException(new Error(`Error fetching rwgConfig: ${body}`));

    return {
      data: undefined,
      requestSuccess: false,
    };
  }

  const data:
    | IGetRwgConfigResponseV1
    | IGetRwgConfigResponseV2
    | IGetRwgConfigErrorResponse = resJson.data.rwgConfig;

  // if we got an error back
  if ("message" in data) {
    return {
      data: undefined,
      requestSuccess: false,
    };
  }

  const dataToSet: Partial<IGetRwgConfigResponseV1 | IGetRwgConfigResponseV2> =
    {
      addressLine: data.addressLine,
      name: data.name,
      ...("schedulerSrc" in data && data.schedulerSrc
        ? { schedulerSrc: data.schedulerSrc }
        : {}),
      ...("clientKey" in data && data.clientKey
        ? { clientKey: data.clientKey }
        : {}),
      ...("flowKey" in data && data.flowKey ? { flowKey: data.flowKey } : {}),
      ...(data.mediaUrl && { mediaUrl: data.mediaUrl }),
    };

  return {
    data: dataToSet as IGetRwgConfigResponseV1 | IGetRwgConfigResponseV2,
    requestSuccess: true,
  };
};

/**
 * Fetches abandonedSessionRwgConfig data for recover-session routes
 */
const fetchAbandonedSessionRwgConfig = async (
  params: IAbandonedSessionRouteParams
): Promise<IClientState> => {
  const { abandonedSessionId } = params;
  const res = await fetch(process.env.REACT_APP_SCHEDULER_API || "", {
    body: JSON.stringify({
      query: `
        query abandonedSessionRwgConfig($abandonedSessionId: ID!) {
          abandonedSessionRwgConfig(abandonedSessionId: $abandonedSessionId) {
            ... on ErrorResponse 
            {
              message
            }
            ... on RwgConfig {
              addressLine
              clientKey
              flowKey
              mediaUrl
              name
              schedulerSrc
            }
          }
        }
      `,
      variables: {
        abandonedSessionId,
      },
    }),
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "Content-Type": "application/json",
    },
    method: "POST",
  });
  const resJson = await res.json();

  if (!res.ok) {
    const body = JSON.stringify(resJson);
    Sentry.setExtra("body", { ...resJson.errors });
    Sentry.captureException(
      new Error(`abandonedSessionRwgConfig failed with ${res.status}: ${body}`)
    );

    return {
      data: undefined,
      requestSuccess: false,
    };
  }

  if (resJson.errors) {
    const body = JSON.stringify(resJson);
    Sentry.setExtra("body", { ...resJson.errors });
    Sentry.captureException(
      new Error(`Error fetching abandonedSessionRwgConfig: ${body}`)
    );

    return {
      data: undefined,
      requestSuccess: false,
    };
  }

  const data:
    | IGetRwgConfigResponseV1
    | IGetRwgConfigResponseV2
    | IGetRwgConfigErrorResponse = resJson.data.abandonedSessionRwgConfig;

  // if we got an error back
  if ("message" in data) {
    return {
      data: undefined,
      requestSuccess: false,
    };
  }

  const dataToSet: Partial<IGetRwgConfigResponseV1 | IGetRwgConfigResponseV2> =
    {
      addressLine: data.addressLine,
      name: data.name,
      ...("schedulerSrc" in data && data.schedulerSrc
        ? { schedulerSrc: data.schedulerSrc }
        : {}),
      ...("clientKey" in data && data.clientKey
        ? { clientKey: data.clientKey }
        : {}),
      ...("flowKey" in data && data.flowKey ? { flowKey: data.flowKey } : {}),
      ...(data.mediaUrl && { mediaUrl: data.mediaUrl }),
    };

  return {
    data: dataToSet as IGetRwgConfigResponseV1 | IGetRwgConfigResponseV2,
    requestSuccess: true,
  };
};

/**
 * Fetches scheduler embed config for a Yelp listing
 */
const fetchYelpSchedulerEmbed = async (
  params: IYelpRouteParams
): Promise<IClientState> => {
  const { yelpListingId } = params;

  if (!yelpListingId) {
    return {
      data: undefined,
      requestSuccess: false,
    };
  }

  const res = await fetch(process.env.REACT_APP_SCHEDULER_API || "", {
    body: JSON.stringify({
      query: `
        query getSchedulerEmbedForYelp($yelpListingId: String!) {
          getSchedulerEmbedForYelp(yelpListingId: $yelpListingId) {
            ... on ErrorResponse {
              message
            }
            ... on YelpSchedulerEmbed {
              schedulerSource
              addressLine
              name
              mediaUrl
              validOrigins
            }
          }
        }
      `,
      variables: {
        yelpListingId,
      },
    }),
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "Content-Type": "application/json",
    },
    method: "POST",
  });

  const resJson = await res.json();

  if (!res.ok) {
    const body = JSON.stringify(resJson);
    Sentry.setExtra("body", { ...resJson.errors });
    Sentry.captureException(
      new Error(`getSchedulerEmbedForYelp failed with ${res.status}: ${body}`)
    );

    return {
      data: undefined,
      requestSuccess: false,
    };
  }

  if (resJson.errors) {
    const body = JSON.stringify(resJson);
    Sentry.setExtra("body", { ...resJson.errors });
    Sentry.captureException(
      new Error(`Error fetching getSchedulerEmbedForYelp: ${body}`)
    );

    return {
      data: undefined,
      requestSuccess: false,
    };
  }

  const data:
    | {
        schedulerSource?: string;
        addressLine?: string;
        name?: string;
        mediaUrl?: string;
        validOrigins?: Array<string>;
      }
    | IGetRwgConfigErrorResponse = resJson.data.getSchedulerEmbedForYelp;

  if ("message" in data || !data.schedulerSource) {
    return {
      data: undefined,
      requestSuccess: false,
    };
  }

  const dataToSet: IGetRwgConfigResponseV2 = {
    addressLine: data.addressLine || "",
    name: data.name || "",
    schedulerSrc: data.schedulerSource,
    ...(data.mediaUrl && { mediaUrl: data.mediaUrl }),
    ...(data.validOrigins && { validOrigins: data.validOrigins }),
  };

  return {
    data: dataToSet,
    requestSuccess: true,
  };
};

/**
 * Fetches configuration data based on route type
 */
export const fetchRwgConfig = async (
  routeParams: IRouteParams
): Promise<IClientState> => {
  switch (routeParams.type) {
    case RouteType.Token:
      return fetchTokenRwgConfig(routeParams);
    case RouteType.AbandonedSession:
      return fetchAbandonedSessionRwgConfig(routeParams);
    case RouteType.Yelp:
      return fetchYelpSchedulerEmbed(routeParams);
    default:
      return {
        data: undefined,
        requestSuccess: false,
      };
  }
};
